import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore, isDarkChromeTheme } from '@/entities/session/model/themeStore';
import { useSessionStore } from '@/entities/session';
import { useOrgAccess } from '@/shared/lib/useOrgAccess';
import { useLmsMe, hasAnyRole } from '@/shared/lib/lms/roles';
import { BrandWordmark, AppIcon, type IconName } from '@/shared/ui';

type NavLeaf = { to: string; labelKey: string; icon?: IconName };
type NavGroup = { kind: 'group'; labelKey: string; icon: IconName; to?: string; children: NavLeaf[] };
type NavItem = { kind: 'link'; to: string; labelKey: string; icon: IconName };
type NavEntry = NavGroup | NavItem;

const ORG_NAV: NavItem = { kind: 'link', to: '/app/organizations', labelKey: 'nav.organizations', icon: 'orgs' };

function buildBaseNav(orgEnabled: boolean, curatorHome: boolean): NavEntry[] {
  const nav: NavEntry[] = [
    { kind: 'link', to: '/app/dashboard', labelKey: 'nav.dashboard', icon: 'home' },
    curatorHome
      ? { kind: 'link', to: '/lms/curate/groups', labelKey: 'nav.groupManager', icon: 'groups' }
      : { kind: 'link', to: '/lms/my', labelKey: 'nav.courses', icon: 'courses' },
  ];
  if (orgEnabled) nav.push(ORG_NAV);
  nav.push(
    {
      kind: 'group',
      labelKey: 'nav.profile',
      icon: 'profile',
      to: '/app/profile',
      children: [
        { to: '/app/activity', labelKey: 'nav.activity', icon: 'activity' },
        { to: '/app/referral', labelKey: 'nav.referral', icon: 'referral' },
        { to: '/app/billing', labelKey: 'nav.billing', icon: 'billing' },
      ],
    },
    { kind: 'link', to: '/app/settings', labelKey: 'nav.settings', icon: 'settings' },
  );
  return nav;
}

const ADMIN_NAV: NavItem[] = [
  { kind: 'link', to: '/app/admin/users', labelKey: 'nav.adminUsers', icon: 'users' },
  { kind: 'link', to: '/app/admin/services', labelKey: 'nav.adminServices', icon: 'services' },
  { kind: 'link', to: '/app/admin/roles', labelKey: 'nav.adminRoles', icon: 'roles' },
];

export function Sidebar() {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const isDark = isDarkChromeTheme(theme);
  const logoutSession = useSessionStore((s) => s.logout);
  const isAdmin = useSessionStore((s) => s.platformRole === 'ADMIN');
  const { enabled: orgEnabled } = useOrgAccess();
  const { data: me } = useLmsMe();
  const curatorHome = hasAnyRole(me?.roles, 'curator') && !hasAnyRole(me?.roles, 'admin_lms');
  const navigate = useNavigate();

  // Organizations are a special-tariff feature — hide the nav entry otherwise.
  const baseNav = buildBaseNav(orgEnabled, curatorHome);
  const NAV_ITEMS: NavEntry[] = isAdmin ? [...baseNav, ...ADMIN_NAV] : [...baseNav];

  const handleLogout = () => {
    logoutSession();
    navigate('/login', { replace: true });
  };

  const linkClass = ({ isActive }: { isActive: boolean }, indent = false) => {
    const base = `flex items-center gap-3 rounded-lg ${indent ? 'pl-9 pr-3' : 'px-3'} py-2 text-sm transition-colors`;
    if (isActive) {
      return base + (isDark ? ' font-medium text-white' : ' bg-primary-50 font-medium text-primary-700');
    }
    return base + (isDark
      ? ' text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/5'
      : ' text-text-secondary hover:bg-surface-tertiary hover:text-text-primary');
  };

  const activeStyle = ({ isActive }: { isActive: boolean }) =>
    isActive && isDark ? { background: 'var(--t-sidebar-active)' } : undefined;

  return (
    <aside
      className="sidebar-themed flex w-60 flex-col border-r transition-colors"
      style={{
        background: 'var(--t-sidebar-bg)',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'var(--t-border)',
      }}
    >
      <div className="sidebar-brand flex h-16 items-center px-6">
        <BrandWordmark
          to="/app"
          heightClass="h-8"
          className="sidebar-logo"
          onDarkSurface={isDark}
        />
      </div>

      <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto px-3 pb-4">
        {NAV_ITEMS.map((item) => {
          if ('kind' in item && item.kind === 'group') {
            return (
              <div key={item.labelKey} className="mt-1">
                {item.to ? (
                  <NavLink
                    to={item.to}
                    className={(s) => linkClass(s)}
                    style={activeStyle}
                  >
                    <AppIcon name={item.icon} size="sm" />
                    {t(item.labelKey)}
                  </NavLink>
                ) : (
                  <div className="flex items-center gap-3 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                    <AppIcon name={item.icon} size="sm" />
                    {t(item.labelKey)}
                  </div>
                )}
                <div className="mt-0.5 flex flex-col gap-0.5">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      className={(s) => linkClass(s)}
                      style={activeStyle}
                    >
                      {child.icon && <AppIcon name={child.icon} size="sm" />}
                      {t(child.labelKey)}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          }
          const leaf = item as NavItem;
          return (
            <NavLink
              key={leaf.to}
              to={leaf.to}
              className={(s) => linkClass(s)}
              style={activeStyle}
            >
              <AppIcon name={leaf.icon} size="sm" />
              {t(leaf.labelKey)}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 pb-4">
        <button
          type="button"
          onClick={handleLogout}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
            isDark
              ? 'text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/10'
              : 'text-text-secondary hover:bg-red-50 hover:text-red-600'
          }`}
        >
          <AppIcon name="logout" size="sm" />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
}
